import { CloseOutlined } from '@ant-design/icons';
import { FCMRequestDoc } from '@gooduncles/gu-app-schema';
import { Button, Descriptions, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FC, useEffect, useMemo, useState } from 'react';

import { formatDate } from 'src/utils/date-util';
import { getFcmRequests } from 'src/utils/firebase/firebase-short-cut';

import classes from './FcmStatusPanel.module.scss';

const { Text } = Typography;

interface FcmStatusPanelProps {
  messageId: string;
  onClose: () => void;
}

const statusTagColor: Record<string, string> = {
  pending: 'orange',
  success: 'green',
  error: 'red',
};

const columns: ColumnsType<FCMRequestDoc> = [
  { title: 'Target ID', dataIndex: 'targetId', key: 'targetId', width: 200, ellipsis: true },
  { title: '서비스', dataIndex: 'targetService', key: 'targetService', width: 100 },
  { title: '대상 유형', dataIndex: 'targetType', key: 'targetType', width: 100 },
  {
    title: '상태',
    dataIndex: 'status',
    key: 'status',
    width: 100,
    render: (status: string) => <Tag color={statusTagColor[status]}>{status}</Tag>,
  },
  {
    title: '발송 시각',
    dataIndex: 'sentAt',
    key: 'sentAt',
    width: 180,
    render: (value: string | null) => (value ? formatDate(new Date(value), 'yyyy-MM-dd HH:mm:ss') : '-'),
  },
  {
    title: '에러',
    dataIndex: 'error',
    key: 'error',
    ellipsis: true,
    render: (error: { code: string; message: string } | null) => (error ? `${error.code}: ${error.message}` : '-'),
  },
];

const FcmStatusPanel: FC<FcmStatusPanelProps> = ({ messageId, onClose }) => {
  const [requests, setRequests] = useState<FCMRequestDoc[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getFcmRequests([['messageId', '==', messageId]])
      .then((data) => setRequests(data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [messageId]);

  const stats = useMemo(() => {
    return requests.reduce(
      (acc, r) => {
        acc.total++;
        if (r.status === 'success') acc.success++;
        else if (r.status === 'error') acc.error++;
        else if (r.status === 'pending') acc.pending++;
        return acc;
      },
      { total: 0, success: 0, error: 0, pending: 0 },
    );
  }, [requests]);

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <h4 className={classes.title}>
          FCM 발송 상태
          <Text className={classes.messageId} copyable={{ text: messageId }}>
            {messageId}
          </Text>
        </h4>
        <Button type='text' size='small' icon={<CloseOutlined />} onClick={onClose} />
      </div>

      <Descriptions size='small' bordered column={4} className={classes.stats}>
        <Descriptions.Item label='전체'>{stats.total}</Descriptions.Item>
        <Descriptions.Item label='성공'>
          <Tag color='green'>{stats.success}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label='실패'>
          <Tag color='red'>{stats.error}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label='대기'>
          <Tag color='orange'>{stats.pending}</Tag>
        </Descriptions.Item>
      </Descriptions>

      <Table
        dataSource={requests}
        columns={columns}
        rowKey='_id'
        size='small'
        loading={loading}
        pagination={{ pageSize: 10, size: 'small', showTotal: (total) => `총 ${total}건` }}
      />
    </div>
  );
};

export default FcmStatusPanel;
